import React, { useState } from 'react';
import { useDispatch } from 'react-redux';
import cn from 'classnames';

import { chargesApi } from '../../../redux/chargesApi';
import DeviceVariant from './DeviceVariant';
import {
  getAssociatedVariants,
  isRecommendToUpdatePlan,
  getUpgradeVariant,
} from '../../../utils/devices';
import { formatMbToGb } from '../../../utils/formatters';
import DeviceUpgradeModal from './DeviceUpgradeModal';
import useGetCharges from '../../../hooks/useGetCharges';
import useNoty from '../../../hooks/useNoty';
import { useGetProductsQuery } from '../../../redux/productsApi';
import { useGetCustomerQuery } from '../../../redux/customersApi';
import { upgradeDeviceMapping } from '../../../constants/upgradeDeviceMapping';
import fetchRequest from '../../../utils/fetchRequest';
import {
  getIsParkingPlan,
  getIsUnlimPlan,
  getPlanAmountFromName,
} from '../../../utils/plans';
import { getDeviceSn, isLowerDataProduct } from '../../../utils/helpers';
import {
  DATE_FORMAT,
  createNextChargeDate,
  getThirtyDaysAgo,
} from '../../../utils/dates';
import { filterNotSubscriptionCharges } from './utils';

import styles from './styles.module.scss';

const planNameMapping = {
  50: 'Starter',
  100: 'Entertainment',
  200: 'Starter',
  300: 'Entertainment',
  450: 'Optimal',
  800: 'Pro',
};

const DeviceVariants = ({
  subscription,
  selectedVariantId,
  isSubCancelled,
  handleSuccessUpdate,
}) => {
  const noty = useNoty();
  const [loading, setLoading] = useState(false);
  const [openUpgradeModal, setOpenUpgradeModal] = useState(false);
  const [upgradeModalType, setUpgradeModalType] = useState('current');
  const [variantToUpgrade, setVariantToUpgrade] = useState({});
  const dispatch = useDispatch();
  const { data: customer } = useGetCustomerQuery();

  const nextChargeDate = createNextChargeDate(
    subscription?.next_charge_scheduled_at,
  );
  const formattedNextChargeDate = nextChargeDate.local().format(DATE_FORMAT);
  const productId = subscription?.external_product_id?.ecommerce;
  const total_usage = subscription?.meta?.usage?.total || 0;
  const upgradeFrom = formatMbToGb(total_usage);
  const lowerDataProduct = isLowerDataProduct(
    subscription?.external_product_id?.ecommerce,
  );
  const upgradeData = findProductForUpgradeByRange(
    parseInt(upgradeFrom),
    variantToUpgrade.variantAmountInGb,
  );
  const { data: productsData } = useGetProductsQuery(
    {
      external_product_ids: productId,
      format_version: '2022-06',
    },
    {
      skip: !productId,
    },
  );
  const { data: upgradeProductData } = useGetProductsQuery(
    {
      external_product_ids: upgradeData?.productId,
      format_version: '2022-06',
    },
    {
      skip: !upgradeData?.productId,
    },
  );
  const upgradeProductVariant = getUpgradeVariant(
    upgradeProductData?.products[0],
    upgradeData,
  );

  const variants = getAssociatedVariants(
    productsData?.products[0],
    subscription?.meta?.variantOptions,
    subscription?.external_variant_id?.ecommerce,
  );

  const deviceSn = getDeviceSn(subscription?.properties);
  const recommendToUpdate = isRecommendToUpdatePlan(subscription?.meta?.usage);
  const formattedVariants = formatVariants();
  const haveUpcomingPlan = findUpcomingPlan();
  const isUnlimPlan = getIsUnlimPlan(subscription?.variant_title);
  const isParkingPlan = getIsParkingPlan(subscription?.variant_title);
  const showVariants = !isParkingPlan && !isUnlimPlan;

  const [charges] = useGetCharges({
    status: ['error', 'pending'],
    created_at_min: getThirtyDaysAgo(),
  });
  const filteredCharges = filterNotSubscriptionCharges(charges);

  const variantClickHandler = (variant) => {
    // not allow upgrade for non standart frequency
    const isStandartSubFrequency =
      subscription.order_interval_frequency?.toString() === '1' ||
      subscription.order_interval_unit === 'month';
    if (isStandartSubFrequency && filteredCharges.length === 0) {
      const modalType =
        variant.isDisabled || variant.currentPlan ? 'upcoming' : 'current';
      setVariantToUpgrade(variant);
      setOpenUpgradeModal(true);
      setUpgradeModalType(modalType);
    } else {
      noty.error({
        value: 'Your account is not eligible for plan changes at this time.',
      });
    }
  };

  const handleCloseModal = () => {
    setOpenUpgradeModal(false);
  };

  const handleSubmitUpgradeForm = async (type) => {
    const addressId = subscription?.address_id;
    const subId = subscription?.id;
    const rechargeUpgradeVariantId = variantToUpgrade.variantId;
    const gbAmount = variantToUpgrade.variantAmountInGb;
    const onetimeVariationId = upgradeData?.variantId;
    const successMessage =
      type === 'current'
        ? `The plan's been upgraded to ${gbAmount}GB. Enjoy using it!`
        : `Successfully upgraded to ${gbAmount}GB for your next cycle. Enjoy your plan!`;

    let url = '';
    let data = {
      device_sn: deviceSn,
      gb: gbAmount,
      oldGb: getPlanAmountFromName(subscription?.variant_title),
      email: customer?.email,
      recharge_sub_id: subId,
      recharge_new_variant_id: rechargeUpgradeVariantId,
      recharge_current_variant_id: subscription?.external_variant_id?.ecommerce,
    };
    if (type === 'current') {
      url = `/subscriptions/${subId}/current_upgrade`;
      data.recharge_onetime_address_id = addressId;
      data.recharge_onetime_variation_id = onetimeVariationId;
    } else {
      url = `/subscriptions/${subId}/upcoming_upgrade`;
    }
    try {
      setLoading(true);
      await fetchRequest.post(url, data);
      handleSuccessUpdate();
      dispatch(chargesApi.util.invalidateTags(['Charge']));
      noty.success({
        value: successMessage,
      });
    } catch (e) {
      noty.error({
        value: 'Something went wrong. Please contact support',
      });
    } finally {
      setOpenUpgradeModal(false);
      setLoading(false);
    }
  };

  return (
    <div className={styles.deviceVariants}>
      <h2 className={styles.deviceVariantsTitle}>Available Plans*</h2>
      {showVariants && (
        <>
          <ul className={styles.deviceVariantsWrapper}>
            {formattedVariants.map((variant, index) => (
              <DeviceVariant
                variant={variant}
                key={index}
                deviceHaveUpcomingPlan={haveUpcomingPlan}
                isSubCancelled={isSubCancelled}
                clickHandle={variantClickHandler}
              ></DeviceVariant>
            ))}
          </ul>
          <DeviceUpgradeModal
            handleSubmit={handleSubmitUpgradeForm}
            variant={variantToUpgrade}
            isOpen={openUpgradeModal}
            type={upgradeModalType}
            handleClose={handleCloseModal}
            nextChargeDate={nextChargeDate}
            isLoading={loading}
            upgradeVariant={upgradeProductVariant}
          />
          <p className={cn('text-s', styles.deviceVariantsNote)}>
            *The plans displayed exclude taxes. You may see a different charge
            amount on your account once the payment is processed.
          </p>
        </>
      )}
      {!showVariants && (
        <p className="text">
          {isUnlimPlan && 'Your subscription already have unlimited plan'}
          {isParkingPlan && 'Your subscription currently at parking plan'}
        </p>
      )}
    </div>
  );

  function findUpcomingPlan() {
    if (!variants) {
      return false;
    }
    return variants.some((variant) => {
      let amount = getPlanAmountFromName(variant?.title).toString();
      let currentPlan = upgradeFrom === amount;
      return variant.external_variant_id === selectedVariantId && !currentPlan;
    });
  }

  function formatVariants() {
    let isDisabled = true;
    let recommendedLabelFlag = false;
    let formattedVariants = variants.map((variant) => {
      let showRecommendedLabel = false;
      let amount = getPlanAmountFromName(variant?.title).toString();
      let planName = planNameMapping[amount];
      let currentPlan = upgradeFrom === amount;
      let upcomingPlan =
        variant.external_variant_id === selectedVariantId && !currentPlan;
      if (!isDisabled && recommendToUpdate && !recommendedLabelFlag) {
        recommendedLabelFlag = true;
        showRecommendedLabel = true;
        if (!isSubCancelled) {
          planName = `from ${formattedNextChargeDate}`;
        }
      }
      if (upcomingPlan) {
        planName = 'Upcoming';
      }

      if (currentPlan && !isSubCancelled) {
        isDisabled = false;
        planName = 'Current';
        currentPlan = true;
      } else if (currentPlan && isSubCancelled) {
        isDisabled = false;
        currentPlan = false;
      }

      return {
        variantAmountInGb: getPlanAmountFromName(variant.title),
        variantId: variant.external_variant_id,
        price: `${parseFloat(variant.prices[0]?.unit_price)}`,
        amount: amount,
        planName: planName,
        isDisabled: isDisabled,
        isDefault: !isDisabled && !currentPlan && !showRecommendedLabel,
        currentPlan: currentPlan,
        recommended: showRecommendedLabel,
        upcomingPlan: upcomingPlan,
      };
    });

    const minimalPlanAmount = lowerDataProduct ? 50 : 200;
    const maximumPlanAmount = 800;
    formattedVariants = formattedVariants.filter((variant) => {
      if (typeof variant.variantAmountInGb !== 'number') {
        return false;
      }

      if (variant.variantId === selectedVariantId) {
        return true;
      }

      if (variant.currentPlan) {
        return true;
      }
      if (
        variant.variantAmountInGb < minimalPlanAmount ||
        variant.variantAmountInGb > maximumPlanAmount
      ) {
        return false;
      }

      return true;
    });
    return formattedVariants;
  }

  function findProductForUpgradeByRange(from, to) {
    return upgradeDeviceMapping.find((mappingItem) => {
      return mappingItem.from === from && mappingItem.to === to;
    });
  }
};

export default DeviceVariants;
